import type { At } from '@atcute/client/lexicons';

export const PANE_TYPE_HOME = 'home';
export const PANE_TYPE_NOTIFICATIONS = 'notifications';
export const PANE_TYPE_PROFILE = 'profile';
export const PANE_TYPE_FEED = 'feed';
export const PANE_TYPE_LIST = 'list';
export const PANE_TYPE_SEARCH = 'search';
export const PANE_TYPE_THREAD = 'thread';
export const PANE_TYPE_BOOKMARKS = 'bookmarks';

export type PaneType =
	| typeof PANE_TYPE_HOME
	| typeof PANE_TYPE_NOTIFICATIONS
	| typeof PANE_TYPE_PROFILE
	| typeof PANE_TYPE_FEED
	| typeof PANE_TYPE_LIST
	| typeof PANE_TYPE_SEARCH
	| typeof PANE_TYPE_THREAD
	| typeof PANE_TYPE_BOOKMARKS;

export const labelizePaneType = (type: PaneType) => {
	switch (type) {
		case PANE_TYPE_HOME:
			return 'Home';
		case PANE_TYPE_NOTIFICATIONS:
			return 'Notifications';
		case PANE_TYPE_PROFILE:
			return 'Profile';
		case PANE_TYPE_FEED:
			return 'Feed';
		case PANE_TYPE_LIST:
			return 'User List';
		case PANE_TYPE_THREAD:
			return 'Thread';
		case PANE_TYPE_BOOKMARKS:
			return 'Saved posts';
		default:
			return 'N/A';
	}
};

export const enum PaneSize {
	SMALL = 'sm',
	MEDIUM = 'md',
	LARGE = 'lg',
}

export const enum SpecificPaneSize {
	INHERIT = 'inherit',
	SMALL = 'sm',
	MEDIUM = 'md',
	LARGE = 'lg',
}

export const enum ProfilePaneTab {
	POSTS = 'posts',
	POSTS_WITH_REPLIES = 'posts_with_replies',
	MEDIA = 'media',
	LIKES = 'likes',
}

export interface BasePaneConfig {
	readonly type: PaneType;
	readonly id: string;
	title: string | null;
	size: SpecificPaneSize;
	uid: At.DID;
}

export interface HomePaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_HOME;
	showReplies: 'follows' | boolean;
	showReposts: boolean;
	showQuotes: boolean;
	/** Stream new posts into the column as they arrive (frequent polling + prepend). */
	realtime?: boolean;
	/**
	 * When realtime is on, jump to the top of the column as new posts arrive.
	 * When off (default), the scroll position is preserved so you stay where you were.
	 */
	pinToTop?: boolean;
}

export interface NotificationsPaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_NOTIFICATIONS;
	mask: number;
	/** Poll frequently and surface new notifications as they arrive. */
	realtime?: boolean;
	/**
	 * When realtime is on, jump to the top of the column as new notifications arrive.
	 * When off (default), the scroll position is preserved so you stay where you were.
	 */
	pinToTop?: boolean;
	soundEnabled?: boolean;
	desktopNotifEnabled?: boolean;
}

export interface ProfilePaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_PROFILE;
	profile: {
		did: At.DID;
		handle: string;
		// name?: string;
	};
	tab: ProfilePaneTab;
	tabVisible: boolean;
}

export interface CustomFeedPaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_FEED;
	feed: {
		uri: string;
		name: string;
	};
	showReplies: boolean;
	showReposts: boolean;
	showQuotes: boolean;
	infoVisible: boolean;
	/** Stream new posts into the column as they arrive (frequent polling + prepend). */
	realtime?: boolean;
	/**
	 * When realtime is on, jump to the top of the column as new posts arrive.
	 * When off (default), the scroll position is preserved so you stay where you were.
	 */
	pinToTop?: boolean;
}

export interface CustomListPaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_LIST;
	list: {
		uri: string;
		name: string;
	};
	showReplies: boolean;
	showQuotes: boolean;
	infoVisible: boolean;
	/** Stream new posts into the column as they arrive (frequent polling + prepend). */
	realtime?: boolean;
	/**
	 * When realtime is on, jump to the top of the column as new posts arrive.
	 * When off (default), the scroll position is preserved so you stay where you were.
	 */
	pinToTop?: boolean;
}

export interface SearchPaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_SEARCH;
	query: string;
	sort: 'top' | 'latest';
	/** Stream new matching posts into the column as they arrive. Opt-in. */
	realtime?: boolean;
	/**
	 * When realtime is on, jump to the top of the column as new posts arrive.
	 * When off (default), the scroll position is preserved so you stay where you were.
	 */
	pinToTop?: boolean;
}

export interface ThreadPaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_THREAD;
	thread: {
		actor: At.DID;
		rkey: string;
	};
}

export interface BookmarksPaneConfig extends BasePaneConfig {
	readonly type: typeof PANE_TYPE_BOOKMARKS;
	/** Stream newly-saved posts into the column as they arrive (frequent polling + prepend). */
	realtime?: boolean;
	/**
	 * When realtime is on, jump to the top of the column as new posts arrive.
	 * When off (default), the scroll position is preserved so you stay where you were.
	 */
	pinToTop?: boolean;
}

export type PaneConfig =
	| HomePaneConfig
	| NotificationsPaneConfig
	| ProfilePaneConfig
	| CustomFeedPaneConfig
	| CustomListPaneConfig
	| SearchPaneConfig
	| ThreadPaneConfig
	| BookmarksPaneConfig;

export interface DeckConfig {
	readonly id: string;
	name: string;
	emoji: string;
	panes: PaneConfig[];
}

export const getPaneSizeWidth = (size: PaneSize): number => {
	if (size === PaneSize.MEDIUM) {
		return 384;
	}
	if (size === PaneSize.LARGE) {
		return 480;
	}

	return 336;
};
